import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarCheck, Plus, Filter } from "lucide-react";
import { PageLoading } from "@/components/shared";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

const today = () => new Date().toISOString().slice(0, 10);
const STATUS_C = { DRAFT: "bg-slate-100 text-slate-500", SCHEDULED: "bg-cyan/10 text-cyan-dark", READY: "bg-emerald-50 text-emerald-600", IN_PROGRESS: "bg-blue-50 text-blue-600", COMPLETED: "bg-slate-100 text-slate-500", CANCELLED: "bg-red-50 text-red-600", LATE: "bg-amber-50 text-amber-700", MISSED: "bg-red-50 text-red-600" };

export default function CareVisits() {
  const [date, setDate] = useState(today());
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [unassigned, setUnassigned] = useState(false);
  const [data, setData] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    setData(null);
    const p = new URLSearchParams({ date });
    if (status) p.set("status", status); if (type) p.set("visit_type", type); if (unassigned) p.set("unassigned", "true");
    api.get(`/care/visits?${p.toString()}`).then((r) => setData(r.data)).catch(() => setData({ visits: [] }));
  }, [date, status, type, unassigned]);

  return (
    <div data-testid="care-visits-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2"><CalendarCheck className="h-6 w-6 text-cyan-dark" /><h1 className="font-heading text-2xl font-extrabold text-navy">Besuche</h1></div>
        <button onClick={() => nav(`/app/employer/pflege/visits/new?date=${date}`)} data-testid="visits-new" className="inline-flex items-center gap-1.5 rounded-full bg-cyan px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-dark"><Plus className="h-4 w-4" /> Neuer Besuch</button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="visits-date" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="visits-filter-status" className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
          <option value="">Alle Status</option>{Object.entries(data?.statuses || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={type} onChange={(e) => setType(e.target.value)} data-testid="visits-filter-type" className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
          <option value="">Alle Arten</option>{Object.entries(data?.visit_types || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <button onClick={() => setUnassigned(!unassigned)} data-testid="visits-filter-unassigned" className={`inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-semibold ${unassigned ? "bg-red-500 text-white" : "bg-slate-100 text-slate-500"}`}><Filter className="h-4 w-4" /> Nicht zugewiesen</button>
      </div>

      {!data ? <PageLoading /> : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
              <tr><th className="px-4 py-3">Zeit</th><th className="px-4 py-3">Patient</th><th className="px-4 py-3">Art</th><th className="px-4 py-3">Mitarbeiter</th><th className="px-4 py-3">Besuch ID</th><th className="px-4 py-3">Status</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.visits.map((v) => (
                <tr key={v.id} onClick={() => nav(`/app/employer/pflege/visits/${v.id}`)} className="cursor-pointer hover:bg-slate-50" data-testid={`visit-row-${v.id}`}>
                  <td className="px-4 py-3 font-semibold text-navy">{v.scheduled_start || "—"}{v.scheduled_end ? `–${v.scheduled_end}` : ""}</td>
                  <td className="px-4 py-3"><div className="font-semibold text-navy">{v.patient_name}</div><div className="font-mono text-[11px] text-slate-400">{v.patient_number}</div></td>
                  <td className="px-4 py-3 text-slate-600">{v.visit_type_label}</td>
                  <td className="px-4 py-3 text-slate-600">{v.worker_name || <span className="text-red-500">Nicht zugewiesen</span>}</td>
                  <td className="px-4 py-3 font-mono text-[11px] text-slate-400">{v.visit_number}</td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_C[v.status] || "bg-slate-100 text-slate-500"}`}>{v.status_label}</span></td>
                </tr>
              ))}
              {data.visits.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400" data-testid="visits-empty">Keine Besuche gefunden.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
